import {
  type Subscription,
  getProductSubscription
} from '@deque/billing-utils';
import { Loader } from '@deque/cauldron-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ContentToast from '~/common/components/ContentToast';
import {
  isSubscriptionExpired,
  ProductSlugs,
  SUPPORTED_TRIAL_PRODUCT_SLUGS
} from '~/common/constants';
import { useAuthContext } from '~/common/contexts/auth';
import { useConfiguration } from '~/common/contexts/Configuration';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useFeatureFlagState } from '~/common/contexts/featureFlags';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { useOnPremValue } from '~/common/contexts/onPrem';
import { useProducts } from '~/common/contexts/products';
import { useServerInfo } from '~/common/contexts/serverInfo';
import useV2EnterpriseMembers from '~/common/hooks/useV2EnterpriseMembers';
import ActiveProducts from '../components/ActiveProducts';
import AvailableProducts from '../components/AvailableProducts';
import useIntegrationConnections from '../hooks/useIntegrationConnections';
import sortProductsOrSubscriptions from '../utils/sort-products-or-subscriptions';

const supportedUserProductSlugs: ProductSlugs[] = [
  ...SUPPORTED_TRIAL_PRODUCT_SLUGS
];
const supportedEnterpriseProductSlugs: ProductSlugs[] = [
  ...SUPPORTED_TRIAL_PRODUCT_SLUGS,
  ProductSlugs.customIntegration,
  ProductSlugs.axeAssistantSlack,
  ProductSlugs.axeAssistantTeams
];

const Products = () => {
  const { t } = useTranslation();
  const { billingUser, user } = useAuthContext();
  const {
    loading: productsLoading,
    error: productsError,
    products,
    getProductBySlug
  } = useProducts();
  const {
    loading: enterprisesLoading,
    error: enterprisesError,
    activeEnterprise,
    isAdmin
  } = useEnterprises();
  const { serverInfo } = useServerInfo();
  const { acceptedMembers, loading: loadingMembers } = useV2EnterpriseMembers({
    token: user?.token,
    enterpriseId: activeEnterprise?.id
  });
  const {
    settings,
    loading: configLoading,
    loadingError: configLoadingError
  } = useConfiguration();
  const {
    data: connectionsData,
    loading: connectionsLoading,
    error: connectionsLoadingError
  } = useIntegrationConnections(activeEnterprise?.id, user?.token);
  const { setContents } = useGlobalToast();
  const hasAxeWatcherV3SelfServe = useFeatureFlagState(
    'axe_devtools_watcher_v3_self_serve'
  );
  const hasAxeReportsTeamsV1 = useFeatureFlagState('axe_reports_teams_v1');

  const hasDequeUniversity = useFeatureFlagState('deque_university');
  const hasAxeMonitorLite = useFeatureFlagState('axe_spider_v1');
  const hasAxeAuditorIntegration = useFeatureFlagState(
    'axe_auditor_integration_v1'
  );
  const hasAxeAuditorUserManagement = useFeatureFlagState(
    'axe_auditor_user_management_v1'
  );

  const isOnPrem = useOnPremValue();

  if (productsError || enterprisesError || configLoadingError) {
    return (
      <ContentToast show={true} type="caution">
        {t('Failed to load products.')}
      </ContentToast>
    );
  }

  useEffect(() => {
    if (connectionsLoadingError) {
      setContents(t('Failed to load integration connections.'));
    }
  }, [connectionsLoadingError]);

  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  const loading = !!(
    productsLoading ||
    enterprisesLoading ||
    !billingUser ||
    !!(activeEnterprise && loadingMembers) ||
    configLoading ||
    connectionsLoading
  );

  if (loading) {
    return (
      <Loader label={t('Loading products...')} tabIndex={-1} ref={loaderRef} />
    );
  }

  const hideExpiredProducts =
    settings?.general.expiredProductVisibility.value === 'none';
  let enterpriseAdmins: string[] = [];
  if (acceptedMembers?.length) {
    enterpriseAdmins = acceptedMembers
      .filter(member => member.is_admin)
      .map(member => member.email);
  }

  let activeSubscriptions = (
    (activeEnterprise
      ? isAdmin
        ? activeEnterprise.subscriptions
        : billingUser.enterprises[0].subscriptions
      : billingUser.subscriptions) as Subscription[]
  ).filter(({ purchase_state }) => {
    if (purchase_state === 'none') {
      return false;
    }

    return true;
  });

  //make Axe Reports visible in constants.ts but filter its subscription with the feature flag
  //remove these changes and the axe_reports_teams_v1 feature flag in a future release
  if (!hasAxeReportsTeamsV1) {
    activeSubscriptions = activeSubscriptions.filter(
      subsciption => subsciption.product_slug !== ProductSlugs.axeReports
    );
  }

  if (!hasAxeAuditorIntegration) {
    activeSubscriptions = activeSubscriptions.filter(
      subsciption =>
        subsciption.product_slug !== ProductSlugs.axeAuditorIntegration
    );
  }

  if (!hasAxeAuditorUserManagement) {
    activeSubscriptions = activeSubscriptions.filter(
      subscription => subscription.product_slug !== ProductSlugs.axeAuditor
    );
  }

  const supportedProductsSlugs: ProductSlugs[] = activeEnterprise
    ? [...supportedEnterpriseProductSlugs]
    : [...supportedUserProductSlugs];

  const unfilteredSubscriptions = activeEnterprise
    ? activeEnterprise.subscriptions
    : billingUser.subscriptions;

  if (hasAxeWatcherV3SelfServe && serverInfo?.jazzband.isEnabled) {
    supportedProductsSlugs.push(ProductSlugs.axeDevToolsWatcher);
  }

  if (hasDequeUniversity) {
    supportedProductsSlugs.push(ProductSlugs.dequeUniversity);
  }

  if (!hasAxeMonitorLite) {
    const productIndex = supportedProductsSlugs.indexOf(ProductSlugs.axeSpider);

    if (productIndex > -1) {
      supportedProductsSlugs.splice(productIndex, 1);
    }
  }

  const availableProducts = products.filter(product => {
    // Self-provisioned mobile enterprise trials are not currently supported
    if (product.slug === ProductSlugs.axeDevToolsMobile && activeEnterprise) {
      return false;
    }

    if (
      product.slug === ProductSlugs.dequeUniversity ||
      product.slug === ProductSlugs.customIntegration ||
      product.slug === ProductSlugs.axeAssistantSlack ||
      product.slug === ProductSlugs.axeAssistantTeams ||
      product.slug === ProductSlugs.advancedRules ||
      product.slug === ProductSlugs.axeDevToolsWatcher
    ) {
      return false;
    }

    const activeSub = getProductSubscription(
      unfilteredSubscriptions,
      product.slug as ProductSlugs
    );

    // Treat as "no subscription" if: none state or no sub
    const hasNoActiveSubscription =
      !activeSub || activeSub.purchase_state === 'none';

    return (
      supportedProductsSlugs.includes(product.slug as ProductSlugs) &&
      hasNoActiveSubscription
    );
  });

  const visibleActiveSubscriptions = hideExpiredProducts
    ? activeSubscriptions.filter(sub => {
        return (
          !isSubscriptionExpired(sub) ||
          getProductBySlug(sub.product_slug)?.has_free_tier
        );
      })
    : activeSubscriptions;

  const showFooter =
    !hideExpiredProducts &&
    !!activeSubscriptions.find(sub => {
      return (
        isSubscriptionExpired(sub) &&
        !getProductBySlug(sub.product_slug)?.has_free_tier
      );
    });

  const axeAuditorIntegrationConnection = connectionsData?.find(
    conn => conn.product_slug === ProductSlugs.axeAuditorIntegration
  );

  return (
    <>
      <ActiveProducts
        subscriptions={sortProductsOrSubscriptions(visibleActiveSubscriptions)}
        isAdmin={isAdmin}
        enterpriseAdmins={enterpriseAdmins}
        showFooter={showFooter}
        axeAuditorIntegrationUrl={
          axeAuditorIntegrationConnection?.base_url ?? ''
        }
      />
      {!isOnPrem && !!availableProducts.length && (
        <AvailableProducts
          enterpriseAdmins={enterpriseAdmins}
          availableProducts={sortProductsOrSubscriptions(availableProducts)}
          isEnterprise={!!activeEnterprise}
          isAdmin={isAdmin}
        />
      )}
    </>
  );
};

export default Products;
